/**
 * Das Gitter der schematischen Karte, gerechnet aus der Route.
 *
 * Hier stand einmal Geografie: die Umrisse von Adria und Schwarzem Meer, neun
 * Ländernamen von der Schweiz bis Bulgarien und ein festes Gitter von 6 bis 30
 * Grad Ost. Für die eine Reise, für die die App gebaut wurde, war das richtig.
 * Für jede zweite ist es falsch, und zwar auf die schlimmere Art: eine leere
 * Karte sagt "ich weiss es nicht", eine falsche behauptet etwas. Eine
 * Atlantikroute liegt bei minus neun Grad, läge also vollständig ausserhalb
 * jenes Gitters - während die Karte die Adria zeichnete und "BULGARIEN"
 * beschriftete.
 *
 * Deshalb gibt es hier keine Geografie mehr, sondern nur noch eine Rechnung:
 * aus dem umschliessenden Rechteck der Route werden Gitterlinien mit einem
 * Abstand, der zur Ausdehnung passt. Was die Karte damit noch zeigt - Route,
 * Etappenpunkte, Gitter mit Gradzahlen -, ist alles aus den Daten belegt.
 *
 * Nachgezeichnete Küstenlinien fehlen bewusst und kommen nicht zurück: sie
 * leisteten ihren Dienst für neun Länder und für keine anderen. Ländernamen
 * ebenfalls; das Feld `land` einer Etappe (etwa "CH→SI") beschreibt einen
 * Übergang entlang einer Strecke und keine Fläche, und ohne Fläche lässt sich
 * daraus keine ehrliche Beschriftung setzen.
 *
 * Die Rechnung kennt den Datumswechsel bei 180 Grad nicht - eine Route, die
 * ihn überquert, hat schon in fitProjection kein sinnvolles Rechteck mehr.
 */

import type { Bounds } from './viewport'

/**
 * Die Abstände, aus denen ein Gitter wählen darf.
 *
 * Die übliche 1-2-5-Leiter: jeder Schritt ist ein Wert, den ein Mensch im Kopf
 * weiterzählen kann. Unten reicht sie bis zu zwei Hundertstelgrad - gut zwei
 * Kilometer, also fein genug für eine Tagesetappe -, oben bis dreissig Grad,
 * womit auch eine Fahrt über einen halben Kontinent noch ein paar Linien
 * bekommt.
 */
const STUFEN = [0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30]

/**
 * Wie viele Felder das Gitter je Achse anstrebt.
 *
 * Sechs trifft die Dichte, die das alte, von Hand gesetzte Gitter hatte: über
 * die Balkanroute lagen drei Breiten- und fünf Längengradlinien. Mehr wäre
 * Millimeterpapier, weniger sagte über die Lage nichts mehr. Getroffen wird
 * die Zahl nicht genau - gewählt wird eine Stufe der Leiter, und die springt.
 */
const ZIEL_FELDER = 6

/**
 * Mehr Linien als das zeichnet keine Achse.
 *
 * Ein Riegel gegen unsinnige Eingaben - eine Etappe mit einer Koordinate von
 * 1e9 kommt aus einer kaputten Datei, nicht aus einer Reise -, damit daraus
 * keine Schleife über Millionen von Linien wird.
 */
const MAX_LINIEN = 40

/** Ein Gitter: die Linien und der Abstand, aus dem sie entstanden sind. */
export interface Grid {
  /** Breitengrade, aufsteigend. */
  lats: number[]
  /** Längengrade, aufsteigend. */
  lons: number[]
  /** Abstand der Breitengrade in Grad. Null, wenn es nichts zu zeichnen gibt. */
  latStep: number
  /** Abstand der Längengrade in Grad. */
  lonStep: number
}

/** Achse einer Gradangabe - sie entscheidet über N/S gegen O/W. */
export type Achse = 'breite' | 'laenge'

/**
 * Zwischen Zahl und Himmelsrichtung: ein geschütztes Leerzeichen.
 *
 * Als Escape und nicht als Zeichen, damit im Quelltext zu sehen ist, dass
 * dort nicht das gewöhnliche Leerzeichen steht - sonst sucht der Nächste
 * lange, warum sein Vergleich fehlschlägt.
 */
const TRENNER = '\u00a0'

const LEER: Grid = { lats: [], lons: [], latStep: 0, lonStep: 0 }

/**
 * Der Abstand, mit dem eine Spannweite ein lesbares Gitter bekommt.
 *
 * Gewählt wird die kleinste Stufe, die nicht feiner ist als ein Sechstel der
 * Spannweite - also lieber ein Feld zu wenig als eines zu viel. Eine
 * Spannweite von null (alle Etappen an derselben Koordinate) bekommt die
 * feinste Stufe; gezeichnet wird davon ohnehin höchstens eine Linie.
 */
export function gridStep(span: number): number {
  const erste = STUFEN[0]!
  const letzte = STUFEN[STUFEN.length - 1]!
  if (!Number.isFinite(span) || span <= 0) return erste
  const roh = span / ZIEL_FELDER
  return STUFEN.find((stufe) => stufe >= roh) ?? letzte
}

/**
 * Die Vielfachen von `step`, die zwischen `min` und `max` liegen.
 *
 * Über ganzzahlige Vielfache und nicht durch Aufaddieren: sonst wandert das
 * Gitter mit dem Ausschnitt, und bei feinen Stufen summiert sich der
 * Gleitkommafehler bis in die Beschriftung. So liegt eine Linie bei 0 Grad
 * genau dann, wenn der Nullmeridian im Bild ist - und nicht irgendwo daneben.
 */
export function gridLines(min: number, max: number, step: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return []
  if (!Number.isFinite(step) || step <= 0 || max < min) return []

  // Der Schlupf fängt den Fall ab, dass eine Grenze rechnerisch knapp neben
  // einem Vielfachen liegt, das sie eigentlich treffen sollte.
  const von = Math.ceil(min / step - 1e-9)
  const bis = Math.floor(max / step + 1e-9)
  if (bis < von || bis - von + 1 > MAX_LINIEN) return []

  const out: number[] = []
  for (let k = von; k <= bis; k++) out.push(runde(k * step))
  return out
}

/**
 * Rundet ein Vielfaches auf eine Stelle, die kein Gleitkommarest mehr trübt,
 * und macht aus einer negativen Null eine gewöhnliche.
 */
function runde(wert: number): number {
  return Number(wert.toFixed(6)) + 0
}

/**
 * Das Gitter für ein umschliessendes Rechteck.
 *
 * @param bounds das Rechteck der Route, wie boundsOf es liefert - oder null,
 *   wenn es keine Route gibt. Dann bleibt das Gitter leer, und die Karte
 *   zeichnet nur, was sie belegen kann: nichts.
 */
export function grid(bounds: Bounds | null): Grid {
  if (!bounds) return LEER
  const [[west, south], [east, north]] = bounds
  if (![west, south, east, north].every((wert) => Number.isFinite(wert))) return LEER

  const latStep = gridStep(north - south)
  const lonStep = gridStep(east - west)
  return {
    lats: gridLines(south, north, latStep),
    lons: gridLines(west, east, lonStep),
    latStep,
    lonStep,
  }
}

/**
 * Die Beschriftung einer Gitterlinie: "46° N", "9° W", "0°".
 *
 * Sie ist der einzige Grund, aus dem das Gitter überhaupt etwas taugt - ohne
 * Zahlen daneben wären es Striche. Die Nachkommastellen kommen aus dem
 * Abstand: bei einem halben Grad Abstand ist "45,5" nötig, bei zwei Grad wäre
 * "46,0" nur Rauschen. Dezimalkomma, wie es sich für die Sprache dieser App
 * gehört.
 *
 * Der Nullpunkt bekommt keine Himmelsrichtung: Äquator und Nullmeridian
 * liegen weder im Norden noch im Süden.
 */
export function gradLabel(wert: number, achse: Achse, step: number): string {
  const zahl = Math.abs(wert).toFixed(nachkommastellen(step)).replace('.', ',')
  if (wert === 0) return `${zahl}°`
  const richtung = achse === 'breite' ? (wert > 0 ? 'N' : 'S') : wert > 0 ? 'O' : 'W'
  return `${zahl}°${TRENNER}${richtung}`
}

function nachkommastellen(step: number): number {
  if (!Number.isFinite(step) || step >= 1) return 0
  if (step <= 0) return 0
  return Math.min(3, Math.ceil(-Math.log10(step)))
}
